import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Stack,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/app/store';
import { PaymentConcept } from '@/types/payment.types';
import PurchaseSummary from './components/PurchasePortal/PurchaseSummary';
import InfoPurchaseSection from './components/PurchasePortal/InfoPurchaseSection';
import ItemsSelector from './components/PurchasePortal/ItemsSelector';
import PurchaseVerificationModal from './components/PurchasePortal/PurchaseVerificationModal';
import { fetchPaymentItems } from '@/features/options/optionsThunks';
import { selectPaymentItems } from '@/features/payments/paymentsSelectors';
import { epaycoService } from '@/services/ePayco/epayco.service';
import { useAuth } from '@/hooks/useAuth';
import { User } from '@/types/user.types';
import PageHeader from '@/components/common/PageHeader';
import AttendantRegistryModal from '@/pages/auth/components/AttendantRegistryModal';

const PurchasePortal: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser, isAuthenticated } = useAuth();
  const temporalUserData = useSelector((state: RootState) => state.temporalUser);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [registryModalOpen, setRegistryModalOpen] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const paymentItems = useSelector(selectPaymentItems);

  useEffect(() => {
    // Validar si el usuario no autenticado tiene datos temporales completos
    if (!isAuthenticated && !temporalUserData.isComplete) {
      setRegistryModalOpen(true);
    }

    if (paymentItems.length === 0) {
      dispatch(fetchPaymentItems());
    }
    const checkSdk = setInterval(() => {
      const ePayco = (window as any).ePayco;
      if (ePayco) {
        setSdkLoaded(true);
        clearInterval(checkSdk);
      }
    }, 100);
    return () => clearInterval(checkSdk);
  }, [dispatch, isAuthenticated, temporalUserData.isComplete]);

  const [itemsToPay, setItemsToPay] = useState<PaymentConcept[]>([]);

  // Obtener datos del usuario (autenticado o temporal)
  const userData: User | null = isAuthenticated
    ? currentUser
    : temporalUserData.data
      ? {
          email: temporalUserData.data.email,
          identifier: temporalUserData.data.cc,
          displayName: 'Usuario Temporal',
          identifierType: 'CC',
          phoneNumber: '',
          site: '',
          createdAt: new Date().toISOString(),
          relatedStudents: temporalUserData.data.students.map(s => ({
            id: s.id,
            name: s.name,
            documentId: s.identifier,
          })),
        } as User
      : null;

  // Obtener estudiantes (de usuario autenticado o temporal)
  const students = isAuthenticated
    ? (currentUser?.relatedStudents || [])
    : (temporalUserData.data?.students.map(s => ({
        id: s.id,
        name: s.name,
        documentId: s.identifier,
      })) || []);

  const handleAddItem = ({ item }: { item: PaymentConcept }): void => {
    const existingItemIndex = itemsToPay.findIndex(i => i.id === item.id && i.studentId === item.studentId);
    if (existingItemIndex !== -1) {
      const updatedItems = itemsToPay.map((i, index) =>
        index === existingItemIndex
          ? { ...i, cant: i.cant + 1 }
          : i
      );
      setItemsToPay(updatedItems);
    } else {
      setItemsToPay([...itemsToPay, { ...item, cant: 1 }]);
    }
  };

  const handleRemoveItem = (id: string, studentId?: string): void => {
    const updatedItems = itemsToPay.filter((i) =>
      !(i.id === id && i.studentId === studentId)
    );
    setItemsToPay(updatedItems);
  };

  const calculateTotal = (): number => {
    return itemsToPay.reduce((sum, item) => sum + item.cant * item.amount, 0);
  };


  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleContinuePayment = async (data: User) => {
    if (!sdkLoaded) {
      alert('Cargando pasarela de pago, intenta de nuevo en un momento');
      return;
    }
    try {
      setIsProcessingPayment(true);
      setModalOpen(false);

      debugger;
      await epaycoService.iniciarPago({
        amount: calculateTotal(),
        description: itemsToPay.map(item => `${item.cant} x ${item.name} - Estudiante: ${item.studentName || 'N/A'}`).join(' | '),
        concepts: itemsToPay.map((item) => {return {
          id: item.id,
          Name: item.name,
          Amount: item.amount,
          studentId: item.studentId,
          cant: item.cant,
        }}
      ),
        customerEmail: data.email,
        customerCC: data.identifier,
        customerName: data.displayName,
        customerPhone: data.phoneNumber,
        customerAddress: data.address,
        customerCity: data.city,
        paymentSite: data.site
      });

      setItemsToPay([]);
      setIsProcessingPayment(false);
    } catch (error) {
      console.error("Error:", error);
      setIsProcessingPayment(false);
      alert("Error al iniciar pago");
    }
  };

  return (
    <Container maxWidth="xl"
      sx={{
        pb: 6,
        px: { xs: 2, md: 3, lg: 4 }
      }}>
      <PageHeader title="Portal de Pagos Institucionales" description="Sistema integral para gestionar todos los pagos académicos y administrativos del José Eusebio Caro de forma rápida, segura y transparente." />

      <InfoPurchaseSection />

      <Paper elevation={0} sx={{
        p: { xs: 2, md: 3, lg: 4 },
        border: '1px solid #E0E0E0',
        borderRadius: 2,
        mx: { xs: 0, md: 4, lg: 16 }
      }}>
        <Box sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          mx: { xs: 0, md: 2, lg: 4 },
          mb: 2
        }}>
          <Typography
            gutterBottom
            fontWeight={500}
            color="text.secondary"
            sx={{ fontSize: { xs: "0.8rem", md: "1rem", lg: "1rem" } }}
          >
            Resumen
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={4}
        >
          <ItemsSelector
            handleAddItem={handleAddItem}
            paymentConcepts={paymentItems || []}
            students={students}
            itemsToPay={itemsToPay} />
          <Box sx={{ flex: 1 }}>
            <PurchaseSummary
              items={itemsToPay}
              total={calculateTotal()}
              onRemoveItem={handleRemoveItem}
              handlePay={handleOpenModal}
            />
          </Box>
        </Stack>
      </Paper>

      <PurchaseVerificationModal
        open={modalOpen}
        onClose={handleCloseModal}
        onContinue={handleContinuePayment}
        userData={userData}
      />

      {/* Attendant Registry Modal - Required for non-authenticated users */}
      <AttendantRegistryModal
        open={registryModalOpen}
        onClose={() => setRegistryModalOpen(false)}
      />

      {/* Loading Overlay for Payment Processing */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          flexDirection: 'column',
          gap: 3
        }}
        open={isProcessingPayment}
      >
        <CircularProgress color="inherit" size={60} thickness={4} />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Procesando pago...
          </Typography>
          <Typography variant="body2" color="inherit" sx={{ opacity: 0.9 }}>
            Por favor espera mientras se abre la pasarela de pago de ePayco
          </Typography>
        </Box>
      </Backdrop>
    </Container>
  );
};

export default PurchasePortal;